import React, {memo, useEffect, useMemo} from 'react';

import {useIssuesContext} from '../../../state/issues/IssuesContext';
import {useMutableFn} from '../../../util/hooks/useMutableFn';
import {
  overrideLineColors,
  overrideLineTitles,
  overrideLineWidths,
  overrideMarks,
} from '../../../util/plotHelpers/chart';
import {type Line} from '../../../util/plotHelpers/types';
import {type RunSetQuery} from '../../../util/queryTypes';
import {useSingleRunModeContext} from '../../MultiRunWorkspace/SingleRunMode';
import {PanelEditorDrawerContent} from '../../WorkspaceDrawer/EditLinePlotPanel/PanelEditorDrawer';
import {useSemanticLegend} from '../../WorkspaceDrawer/Settings/semanticLegend/useSemanticLegend';
import {detectLineAnomalies} from '../alerts';
import {useDerivedLinesConfiguration} from '../config/useDerivedLinesConfiguration';
import {usePanelConfigContext} from '../PanelConfigContext';
import {usePanelInteractionContext} from '../PanelInteractionContext';
import {usePanelZoom2} from '../zoom/PanelZoomContext2';
import {useIsInLoadedReportContext} from './../../../pages/ReportPage/LoadedReportContext';
import {isSystemMetric} from './../../../util/metrics';
import {ConfigWrapper} from './../Config';
import {GraphWrapper} from './../Graph';
import {usePanelTimeContext} from './../PanelTimeContext';
import {type RunsLinePlotPanelProps} from './../types';
import {removeBoundaryPoints} from './lineFilters/removeBoundaryPoints';
import {useCachedBucketedData} from './useCachedBucketedData';
import {useLines} from './useLines';
import {checkIfUsingTimeConversion} from './useLinesUtils';
import {useTimeConvertedLines} from './useTimeConvertedLines';

type PanelRunsLinePlotProps = RunsLinePlotPanelProps;
const emptySemanticSettings = {};
const emptyRunSets: RunSetQuery[] = [];
const featureSupport = {
  grouping: false,
};

const PanelOutliersRunsLinePlotComp: React.FC<
  PanelRunsLinePlotProps
> = props => {
  const {fullScreenNumBuckets, numBucketsByPanelWidth} =
    usePanelInteractionContext();
  const isInReport = useIsInLoadedReportContext();
  const {timeFactor} = usePanelTimeContext();

  const {config: runsLinePlotConfig} = props;
  const {
    activeErrorDrawer,
    annotationActive,
    fetchTimestamps,
    getIssuesByCacheKey,
    issueRampStatus,
  } = useIssuesContext();

  const {entityName, projectName} = usePanelConfigContext();
  const {isSingleRun} = useSingleRunModeContext();
  const {queryZoom} = usePanelZoom2();

  const cacheKey = useMemo(() => {
    return `${runsLinePlotConfig.xAxis ?? '_step'}-${queryZoom.xAxisMin}-${
      queryZoom.xAxisMax
    }`;
  }, [runsLinePlotConfig.xAxis, queryZoom]);

  /**
   * When there's an error on the fetch we want to avoid showing any issues in the charts. I think it's unlikely you can hit this is production but I hit this while hardcoding fetch errors during development and want to make sure to preclude it.
   */
  const issueLines = useMemo(() => {
    return annotationActive && !activeErrorDrawer
      ? getIssuesByCacheKey(cacheKey)
      : [];
  }, [annotationActive, cacheKey, getIssuesByCacheKey, activeErrorDrawer]);

  const fetchTimestampsFn = useMutableFn(fetchTimestamps);

  useEffect(() => {
    if (runsLinePlotConfig.xAxis == null) {
      return;
    }
    if (issueRampStatus === 'enabled') {
      fetchTimestampsFn({
        xAxis: runsLinePlotConfig.xAxis ?? '_step',
        zoom: [queryZoom.xAxisMin ?? null, queryZoom.xAxisMax ?? null],
      });
    }
  }, [fetchTimestampsFn, runsLinePlotConfig.xAxis, queryZoom, issueRampStatus]);

  const semanticLegendSettings = useSemanticLegend(
    entityName,
    projectName,
    props.pageQuery.runSets?.[0]?.filters,
    props.pageQuery.runSets?.[0]?.sort,
    undefined,
    undefined,
    props.semanticLegendSettings
  );

  // queryZoomRange uses native x-axis values for all x-axes including _absolute_runtime.
  // The backend now handles conversion of _absolute_runtime minX/maxX to step bounds.
  const queryZoomRange = useMemo(() => {
    return {
      min: queryZoom.xAxisMin ?? null,
      max: queryZoom.xAxisMax ?? null,
    };
  }, [queryZoom]);

  const {data, loading, error} = useCachedBucketedData({
    isInReport,
    pageQuery: props.pageQuery,
    runsLinePlotConfig,
    queryZoomRange,
    numBuckets: numBucketsByPanelWidth,
    fullScreenNumBuckets,
  });

  /**
   * When deriving the lines to show the run data on the plot we need to merge configuration data from multiple sources in order to draw the lines correctly.
   */
  const runSets = props.pageQuery.runSets ?? emptyRunSets;

  const derivedLinesConfiguration = useDerivedLinesConfiguration({
    customRunColors: props.customRunColors,
    customRunNames: props.customRunNames,
    semanticLegendSettings: semanticLegendSettings ?? emptySemanticSettings,
    runsLinePlotConfig,
    /**
     * I don't know what to do if there are multiple runsets - generally we can assume one
     */
    runSets,
    zoomTimestep: timeFactor,
  });

  const needsTimeConversion = checkIfUsingTimeConversion(
    derivedLinesConfiguration
  );

  const linesInitial = useLines(
    derivedLinesConfiguration,
    data.histories.data,
    data.isAggregated,
    {
      isBackendLinesData: data.allRunsetsHaveBackendLines,
      isInReport,
      numBucketsByPanelWidth: numBucketsByPanelWidth ?? 1000,
    }
  );

  const baseLines = useMemo(
    () =>
      removeBoundaryPoints(
        linesInitial,
        {
          min: queryZoom.xAxisMin ?? null,
          max: queryZoom.xAxisMax ?? null,
        },
        runsLinePlotConfig.xAxis === '_timestamp' ? 1000 : 1
      ),
    [linesInitial, queryZoom, runsLinePlotConfig.xAxis]
  );

  const lines = useTimeConvertedLines({
    lines: baseLines,
    needsTimeConversion,
    allRunsetsHaveBackendLines: data.allRunsetsHaveBackendLines,
    backendLinesDomain: data.backendLinesDomain,
    zoomTimestep: timeFactor,
  });

  const validHistoriesLength = data.histories.data
    .flatMap(data => data.history)
    .filter(history => history.length > 0).length;
  const hasSystemMetrics =
    runsLinePlotConfig.metrics?.some(isSystemMetric) ?? false;
  useEffect(() => {
    detectLineAnomalies({
      numLines: lines.length,
      numHistories: validHistoriesLength,
      isSystemMetric: hasSystemMetrics,
    });
  }, [lines.length, validHistoriesLength, hasSystemMetrics]);

  const linesWithTitleOverride = useMemo(() => {
    if (runsLinePlotConfig.overrideSeriesTitles == null) {
      return lines;
    }
    return overrideLineTitles(
      lines as Line[],
      runsLinePlotConfig.overrideSeriesTitles,
      !isSingleRun
    );
  }, [lines, runsLinePlotConfig.overrideSeriesTitles, isSingleRun]);
  const linesWithColorOverride = useMemo(() => {
    if (runsLinePlotConfig.overrideColors == null) {
      return linesWithTitleOverride;
    }
    return overrideLineColors(
      linesWithTitleOverride as Line[],
      runsLinePlotConfig.overrideColors,
      !isSingleRun
    );
  }, [linesWithTitleOverride, runsLinePlotConfig.overrideColors, isSingleRun]);

  const linesWithMarkOverride = useMemo(() => {
    if (runsLinePlotConfig.overrideMarks == null) {
      return linesWithColorOverride;
    }
    return overrideMarks(
      linesWithColorOverride as Line[],
      runsLinePlotConfig.overrideMarks,
      !isSingleRun
    );
  }, [linesWithColorOverride, runsLinePlotConfig.overrideMarks, isSingleRun]);

  const linesWithWidthOverride = useMemo(() => {
    if (runsLinePlotConfig.overrideLineWidths == null) {
      return linesWithMarkOverride;
    }
    return overrideLineWidths(
      linesWithMarkOverride as Line[],
      runsLinePlotConfig.overrideLineWidths,
      !isSingleRun
    );
  }, [
    linesWithMarkOverride,
    runsLinePlotConfig.overrideLineWidths,
    isSingleRun,
  ]);

  if (props.showDrawer) {
    return (
      <PanelEditorDrawerContent
        config={props.config}
        updateConfig={props.updateConfig}
        pageQuery={props.pageQuery}
        // @ts-ignore
        data={data}
        lines={linesWithWidthOverride}
        query={props.query}
      />
    );
  }

  if (props.configMode) {
    return (
      <ConfigWrapper
        {...props}
        // @ts-ignore
        data={data}
        hasError={!!error}
        issueLines={issueLines}
        featureSupport={featureSupport}
        // @ts-ignore
        lines={linesWithWidthOverride}
        lineCount={lines.length}
        loading={loading}
      />
    );
  }

  return (
    <GraphWrapper
      {...props}
      // @ts-ignore
      data={data}
      hasError={!!error}
      issueLines={issueLines}
      lineCount={lines.length}
      // @ts-ignore
      lines={linesWithWidthOverride}
      loading={loading}
      runNameTruncationType={
        props.pageQuery.runSets?.[0]?.runNameTruncationType
      }
    />
  );
};

export const PanelOutliersRunsLinePlot: React.FC<PanelRunsLinePlotProps> = memo(
  PanelOutliersRunsLinePlotComp
);
PanelOutliersRunsLinePlot.displayName = 'PanelOutliersRunsLinePlot';
